import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { MapPin } from "lucide-react";

// Same map as the backend so a `?country=XX` URL override resolves to a name.
const COUNTRY_NAMES = {
  US: "the USA", GB: "the UK", CA: "Canada", AU: "Australia", NZ: "New Zealand",
  MA: "Morocco", DZ: "Algeria", TN: "Tunisia", EG: "Egypt", SA: "Saudi Arabia",
  AE: "the UAE", QA: "Qatar", KW: "Kuwait", BH: "Bahrain", OM: "Oman",
  JO: "Jordan", LB: "Lebanon",
  ES: "Spain", FR: "France", DE: "Germany", IT: "Italy", PT: "Portugal",
  NL: "the Netherlands", BE: "Belgium", CH: "Switzerland", AT: "Austria",
  IE: "Ireland", DK: "Denmark", SE: "Sweden", NO: "Norway", FI: "Finland",
  TR: "Turkey", IL: "Israel",
  IN: "India", SG: "Singapore", JP: "Japan", KR: "South Korea", HK: "Hong Kong",
  MX: "Mexico", BR: "Brazil",
  ZA: "South Africa", NG: "Nigeria", KE: "Kenya",
};

/**
 * Renders a small "Built for SMBs in <country>" chip above the hero eyebrow.
 * Country source order:
 *   1. `?country=FR` URL param  (handy for previews / marketing demos)
 *   2. `/api/geo` — backed by Cloudflare's `CF-IPCountry` request header
 * Renders nothing when neither source returns a country (clean degrade).
 */
export const GeoHeroLine = () => {
  const { t } = useTranslation();
  const [country, setCountry] = useState(null);
  const API = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    // 1. URL override
    const params = new URLSearchParams(window.location.search);
    const override = (params.get("country") || "").trim().toUpperCase();
    if (override && COUNTRY_NAMES[override]) {
      setCountry(COUNTRY_NAMES[override]);
      return;
    }

    // 2. Cloudflare-backed lookup
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/geo`, { cache: "no-store" });
        if (!r.ok) return;
        const data = await r.json();
        if (!cancelled && data?.countryName) setCountry(data.countryName);
      } catch (_) {
        /* silent — hide line on error */
      }
    })();
    return () => { cancelled = true; };
  }, [API]);

  if (!country) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="mb-5 inline-flex items-center gap-2 rounded-full border border-foreground/10 bg-foreground/[0.04] backdrop-blur px-3 py-1.5 text-[11px] font-medium text-foreground/85"
      data-testid="hero-geo-line"
    >
      <span className="relative flex h-2 w-2">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400/70 opacity-75" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
      </span>
      <MapPin className="h-3 w-3 text-emerald-400" strokeWidth={2} />
      <span>
        {t("hero.geoLine", { country })}
      </span>
    </motion.div>
  );
};

export default GeoHeroLine;
